import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { config, KeyUri } from "../key";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const OrderHistory = ({ userid }) => {
  const navigate = useNavigate();
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState({
    route: null,
    date: new Date()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingType, setBookingType] = useState("cab");
  const [cabOrders, setCabOrders] = useState([]);
  const [hotelOrders, setHotelOrders] = useState([]);
  const [tourOrders, setTourOrders] = useState([]);
  const [wellnessOrders, setWellnessOrders] = useState([]);

  const ordersPerPage = 5;

  useEffect(() => {
const fetchData = async () => {
  try {
    if (!userid) return;

    const tokenUser =
      localStorage.getItem("tokenUser") || localStorage.getItem("tokenuser");
    if (!tokenUser) return;

    const token = tokenUser.replace(/^"|"$/g, "");
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    };

    const [cabRes, hotelRes, tourRes, wellnessRes] = await Promise.all([
      axios.get(`${KeyUri.BACKEND_URI}/get-bookings-user/${userid}`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${KeyUri.BACKEND_URI}/bookings/user/${userid}`, { headers }).catch(() => ({ data: { data: [] } })),
      axios.get(`${KeyUri.BACKEND_URI}/tour-booking/${userid}`, { headers }).catch(() => ({ data: { data: [] } })),
      axios.get(`${KeyUri.BACKEND_URI}/wellness-booking/user/${userid}`, { headers }).catch(() => ({ data: { data: [] } })),
    ]);

    const cabData =
      cabRes?.data?.map((o) => ({
        ...o,
        date: moment(o.date).format("YYYY-MM-DD"),
      })) || [];
    setCabOrders(cabData);

    const hotelData =
      hotelRes?.data?.data?.map((b) => ({
        ...b,
        date: moment(b.createdAt).format("YYYY-MM-DD"),
      })) || [];
    setHotelOrders(hotelData);

    const tourData = tourRes?.data?.data?.map((t) => ({
      ...t,
      date: moment(t.date).format("YYYY-MM-DD"),
    })) || [];
    setTourOrders(tourData);

    const wellnessData =
      wellnessRes?.data?.data?.map((w) => ({
        ...w,
        date: moment(w.createdAt).format("YYYY-MM-DD"),
      })) || [];
    setWellnessOrders(wellnessData);

    // Pick the default tab based on the user's most recent booking
    const candidates = [
      { type: "cab", latest: getLatestDate(cabData, "date") },
      { type: "hotel", latest: getLatestDate(hotelData, "date") },
      { type: "tour", latest: getLatestDate(tourData, "date") },
      { type: "wellness", latest: getLatestDate(wellnessData, "date") },
    ].filter((c) => c.latest !== null);

    if (candidates.length > 0) {
      candidates.sort((a, b) => b.latest - a.latest);
      setBookingType(candidates[0].type);
    }
    // If the user has no bookings at all, bookingType stays "cab" (the existing default)

  } catch (err) {
    console.error("Error fetching history:", err);
  } finally {
    setIsLoading(false);
  }
};

    fetchData();
  }, [userid]);

  const getLatestDate = (list, dateField) => {
  if (!list || list.length === 0) return null;
  const dates = list
    .map((o) => new Date(o[dateField]))
    .filter((d) => !isNaN(d.getTime()));
  if (dates.length === 0) return null;
  return new Date(Math.max(...dates));
};

  const getBaseOrders = () => {
    if (bookingType === "cab") return cabOrders;
    if (bookingType === "hotel") return hotelOrders;
    if (bookingType === "tour") return tourOrders;
    return wellnessOrders;
  };

  useEffect(() => {
    const base = getBaseOrders();
    const filtered = base.filter((order) =>
      Object.values(order).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [searchTerm, bookingType, cabOrders, hotelOrders, tourOrders, wellnessOrders]);

  const sortOrders = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredOrders].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setFilteredOrders(sorted);
  };

  // Pagination calculations
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const handleRowClick = (order) => {
    if (bookingType === "cab") {
      navigate("/detailedbooking", { state: { order } });
    } else if (bookingType === "hotel") {
      navigate(`/hotel-booking/${order._id}`);
    } else if (bookingType === "tour") {
  navigate(`/tour-booking/${order._id}`);
    } else if (bookingType === "wellness") {
      navigate(`/wellness-booking/${order._id}`);
    }
  };

  const handleDateSubmit = async () => {
    setIsSubmitting(true);
    try {
      const formattedDate = moment(selectedBooking.date).format('YYYY-MM-DD');
      const formData = {
        from: selectedBooking?.route?.route?.from_location?._id,
        to: selectedBooking?.route?.route?.to_location?._id,
        pickupStreet: selectedBooking?.route?.pickup_point,
        dropStreet: selectedBooking?.route?.drop_point,
        date: formattedDate,
        seats: selectedBooking?.route?.vehicleId?.category
      };
      localStorage.setItem('Selected Data', JSON.stringify(formData));
      navigate("/sharedcabs");
      setShowModal(false);
    } catch (error) {
      console.error("Error storing booking:", error);
      alert('Error saving details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const date_in_normal_form = (rideDate) => {
    if (!rideDate) return "-";
    const rideDateObj = new Date(rideDate);
    const options = {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };
    return rideDateObj.toLocaleDateString("en-GB", options).replace(",", "");
  };

  const getColumnHeader = () => {
    switch (bookingType) {
      case "cab": return "Ride Date";
      case "hotel": return "Check-in";
      case "tour": return "Tour Date";
      case "wellness": return "Preferred Date";
      default: return "Date";
    }
  };

  const getFromValue = (order) => {
    switch (bookingType) {
      case "cab": return order?.route?.from_location?.name;
      case "hotel": return order?.propertyId?.propertyName || order?.accommodationId?.name || "Hotel";
      case "tour": return order?.tourId?.title || "Tour";
      case "wellness": return order?.packageId?.title || order?.packageTitle || "Wellness Program";
      default: return "-";
    }
  };

  const getToValue = (order) => {
    switch (bookingType) {
      case "cab": return order?.route?.to_location?.name;
      case "hotel": return order?.propertyId?.location?.city || "-";
      case "tour": return order?.tourId?.destination || "-";
      case "wellness": return order?.packageId?.duration || order?.duration || "-";
      default: return "-";
    }
  };

  const getEventDate = (order) => {
    switch (bookingType) {
      case "cab": return order.ride_date_and_time;
      case "hotel": return order.checkIn;
      case "tour": return order.date;
      case "wellness": return order.startDate || order.preferredDate;
      default: return null;
    }
  };

  const getFare = (order) => {
    if (bookingType === "cab") return order.payment_amount;
    if (bookingType === "wellness") return order?.totalPrice || order?.packageId?.price || "-";
    return order.amount || order.amountPaid;
  };

  const getStatus = (order) => {
    if (bookingType === "cab") {
      return order.payment_status === "captured" ? "Paid" : "Pending";
    }
    if (bookingType === "wellness") {
      return order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : "Inquiry";
    }
    return order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : "Confirmed";
  };

  const getStatusClass = (order) => {
    if (bookingType === "cab") {
      return order.payment_status === "captured"
        ? "bg-green-100 text-green-800"
        : "bg-red-100 text-red-800";
    }
    if (bookingType === "wellness") {
      return "bg-blue-100 text-blue-800";
    }
    if (order.status === "cancelled" || order.status === "failed") {
      return "bg-red-100 text-red-800";
    }
    return "bg-green-100 text-green-800";
  };

  return (
    <div className="min-h-[80vh] p-4 flex items-center justify-center mb-24 " style={{ fontFamily: 'Lato, sans-serif' }}>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-semibold mb-4">
              Select Date for {selectedBooking.route?.from_location?.name} to {selectedBooking.route?.to_location?.name}
            </h2>
            <DatePicker
              selected={selectedBooking.date}
              onChange={(date) => setSelectedBooking(prev => ({
                ...prev,
                date: date
              }))}
              minDate={new Date()}
              className="w-full p-2 border rounded mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDateSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
              >
                {isSubmitting ? 'Searching...' : 'Search Cabs'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-6xl">
        <div className="p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            Order History
          </h2>
          <div className="mb-4 flex justify-between items-center">
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
            <select
              value={bookingType}
              onChange={(e) => {
                setBookingType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
            >
              <option value="cab">Cabs</option>
              <option value="hotel">Hotels</option>
              <option value="tour">Tours</option>
              <option value="wellness">Wellness / Retreats</option>
            </select>
          </div>
          {isLoading ? (
            <div className="text-center py-10">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
              <p className="mt-2 text-gray-500">Loading orders...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
               <thead>
  <tr className="bg-gray-50">
    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {bookingType === "wellness" ? "Program" : "From"}
    </th>
    <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {bookingType === "wellness" ? "Duration" : "To"}
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Booked Date
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {getColumnHeader()}
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {bookingType === "wellness" ? "Price" : "Fare"}
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Status
    </th>
    {/* ✅ Actions column for both cab and wellness */}
    {(bookingType === "cab" || bookingType === "wellness") && (
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {bookingType === "wellness" ? "Payment" : "Actions"}
      </th>
    )}
  </tr>
</thead>

<tbody>
  {currentOrders.length > 0 ? (
    currentOrders.map((order, index) => (
      <tr
        key={index}
        className="border-b cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => handleRowClick(order)}
      >
        <td className="px-2 py-4 text-gray-700">
          {getFromValue(order)}
        </td>
        <td className="px-5 py-4 text-gray-700">
          {getToValue(order)}
        </td>
        <td className="px-1 py-4 text-gray-700">{order.date}</td>
        <td className="px-6 py-4 text-gray-700">
          {date_in_normal_form(getEventDate(order))}
        </td>
        <td className="px-5 py-4 text-gray-700">
          {getFare(order) !== "-" ? `₹${getFare(order)}` : "-"}
        </td>
        <td className={`px-6 py-4 font-semibold rounded-lg px-3 ${getStatusClass(order)}`}>
          {getStatus(order)}
        </td>

        {/* ✅ Wellness payment button */}
        {bookingType === "wellness" && (
  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
    {order.paymentStatus === 'partially_paid' ? (
      <button
        onClick={async (e) => {
          e.stopPropagation();
          try {
            const tokenUser = localStorage.getItem("tokenUser") || localStorage.getItem("tokenuser");
            const token = tokenUser.replace(/^"|"$/g, "");

            
            if (order.remainingPaymentLink) {
              window.open(order.remainingPaymentLink, '_blank');
            } else {
              const res = await axios.post(
                `${KeyUri.BACKEND_URI}/wellness-booking/${order._id}/generate-payment-link`,
                {},
               // { headers: { Authorization: `Bearer ${token}` } }
              );
              window.open(res.data.link, '_blank');
            }
          } catch (err) {
            console.error('Error:', err);
            alert('Failed to open payment link. Please try again.');
          }
        }}
        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm font-semibold whitespace-nowrap"
      >
        Pay Balance ₹{order.totalPrice - order.amountPaid}
      </button>
    ) : order.paymentStatus === 'paid' ? (
      <span className="text-green-600 font-semibold text-sm">Fully Paid</span>
    ) : (
      <span className="text-gray-400 text-sm">-</span>
    )}
  </td>
)}

        {/* ✅ Cab repeat button */}
        {bookingType === "cab" && (
          <td className="px-6 py-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedBooking({ route: order, date: new Date() });
                setShowModal(true);
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Repeat
            </button>
          </td>
        )}
      </tr>
    ))
  ) : (
    <tr>
      <td
        colSpan={bookingType === "cab" || bookingType === "wellness" ? 7 : 6}
        className="text-center py-6 text-gray-500"
      >
        No orders found
      </td>
    </tr>
  )}
</tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center mt-4 items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-lg bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
                  >
                    Previous
                  </button>

                  <div className="overflow-x-auto max-w-[60vw]">
                    <div className="flex gap-1 w-max">
                      {Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i + 1}
                          onClick={() => setCurrentPage(i + 1)}
                          className={`mx-1 px-3 py-1 rounded-lg text-sm ${
                            currentPage === i + 1
                              ? "bg-teal-500 text-white"
                              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-lg bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;



/*
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { config, KeyUri } from "../key";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const OrderHistory = ({ userid }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState({
    route: null,
    date: new Date()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ordersPerPage = 5;

  useEffect(() => {
  const fetchData = async () => {
    try {
      if (!userid) return;

      const tokenUser = localStorage.getItem("tokenUser") || localStorage.getItem("tokenuser");
      if (!tokenUser) {
        console.warn("No token found, skipping request");
        return;
      }

      const token = tokenUser.replace(/^"|"$/g, ""); // strip quotes if stored as string

      const response = await axios.get(
        `${KeyUri.BACKEND_URI}/get-bookings-user/${userid}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      const formattedOrders =
        response?.data?.map((order) => ({
          ...order,
          date: moment(order.date).format("YYYY-MM-DD"),
        })) || [];

      setOrders(formattedOrders);
      setFilteredOrders(formattedOrders);

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  fetchData();
}, [userid]);


  useEffect(() => {
    const filtered = orders.filter((order) =>
      Object.values(order).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [searchTerm, orders]);

  const sortOrders = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredOrders].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setFilteredOrders(sorted);
  };

  //// Pagination calculations
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const handleRowClick = (order) => {
    navigate('/detailedbooking', { state: { order: order } });
  };

  const handleDateSubmit = async () => {
    setIsSubmitting(true);
    try {
      const formattedDate = moment(selectedBooking.date).format('YYYY-MM-DD');
      const formData = {
        from: selectedBooking?.route?.route?.from_location?._id,
        to: selectedBooking?.route?.route?.to_location?._id,
        pickupStreet: selectedBooking?.route?.pickup_point,
        dropStreet: selectedBooking?.route?.drop_point,
        date: formattedDate,
        seats: selectedBooking?.route?.vehicleId?.category
      };
      localStorage.setItem('Selected Data', JSON.stringify(formData));
      navigate("/sharedcabs");
      setShowModal(false);
    } catch (error) {
      console.error("Error storing booking:", error);
      alert('Error saving details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const date_in_normal_form = (rideDate) => {
    const rideDateObj = new Date(rideDate);
    const options = {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };
    return rideDateObj.toLocaleDateString("en-GB", options).replace(",", "");
  };

  return (
    <div className="min-h-[80vh] p-4 flex items-center justify-center mb-24" style={{ fontFamily: 'Lato, sans-serif' }}>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-semibold mb-4">
              Select Date for {selectedBooking.route?.from_location?.name} to {selectedBooking.route?.to_location?.name}
            </h2>
            <DatePicker
              selected={selectedBooking.date}
              onChange={(date) => setSelectedBooking(prev => ({
                ...prev,
                date: date
              }))}
              minDate={new Date()}
              className="w-full p-2 border rounded mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDateSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
              >
                {isSubmitting ? 'Searching...' : 'Search Cabs'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-6xl">
        <div className="p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            Order History
          </h2>
          <div className="mb-4 flex justify-between items-center">
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>
          {isLoading ? (
            <div className="text-center py-10">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
              <p className="mt-2 text-gray-500">Loading orders...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th
                        onClick={() => sortOrders("from")}
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      >
                        From
                      </th>
                      <th
                        onClick={() => sortOrders("to")}
                        className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      >
                        To
                      </th>
                      <th
                        onClick={() => sortOrders("date")}
                        className="px-6 lg:px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      >
                        Booked Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ride Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fare
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.length > 0 ? (
                      currentOrders.map((order, index) => (
                        <tr 
                          key={index} 
                          className="border-b cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleRowClick(order)}
                        >
                          <td className="px-2 py-4 text-gray-700">
                            {order?.route?.from_location?.name}
                          </td>
                          <td className="px-5 py-4 text-gray-700">
                            {order?.route?.to_location?.name}
                          </td>
                          <td className="px-1 py-4 text-gray-700">{order?.date}</td>
                          <td className="px-6 py-4 text-gray-700">
                            {date_in_normal_form(order?.ride_date_and_time)}
                          </td>
                          <td className="px-5 py-4 text-gray-700">
                          ⟨₹⟩{order.payment_amount}
                          </td>
                          <td className={`px-6 py-4 font-semibold ${
                            order?.payment_status === "captured"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          } rounded-lg px-3`}>
                            {order?.payment_status === "captured" ? "Paid" : "Pending"}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBooking({
                                  route: order,
                                  date: new Date()
                                });
                                setShowModal(true);
                              }}
                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              Repeat
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-6 text-gray-500">
                          No orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {totalPages > 1 && (
              
                <div className="flex justify-center mt-4 items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-lg bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
                  >
                    Previous
                  </button>


<div className="overflow-x-auto max-w-[60vw]">
    <div className="flex gap-1 w-max">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`mx-1 px-3 py-1 rounded-lg text-sm ${
                        currentPage === i + 1
                          ? "bg-teal-500 text-white"
                          : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  </div>
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => 
                      Math.min(p + 1, totalPages)
                    )}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-lg bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
                  >
                    Next
                  </button>
                </div>
            
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;*/